import React, {FC, useEffect, useMemo, useState} from "react";
import {addressService} from "../../../store/actions";
import {Link, useParams} from "react-router-dom";
import useCommon from "../../../common/hooks/useCommon";
import {
    formatNumber,
    formatTimestamp,
    formatUnit,
    getFiat,
    getPlural,
    getTimeFromNow,
    getTimeLang,
    serializeQuery,
    toGwei,
    toXDCNumber,
    truncate,
} from "../../../common/utils/function";
import BTable2 from "../../../components/b-table2";
import AddressName from "../address-name";
import BTooltip from "../../../components/b-tooltip";
import TxStatus from "../tx-status";
import BPrice from "../b-price";
import {useTranslation} from "react-i18next";
import MethodName from "../method-name";
import BCopy from "../../../components/b-copy";
import CSVConverter from "../../../components/b-CSV-converter";
import {Dropdown} from "react-bootstrap";
import InOut from "../in-out";
import {useSelector} from "react-redux";

interface TransactionAccountListProps {
    count: number;
    transactionData: any;
}

interface TransactionListData {
    hash: string;
    block: number;
    timestamp: string | number;
    type: any;
    from: any;
    to: any;
    value: string | number;
    gas_price: string | number;
    gas_used: string | number;
    transaction_types: any;
}

const TransactionAccountList: FC<TransactionAccountListProps> = (props) => {
    const {address} = useParams();
    const {getXDC, get0x} = useCommon();
    const {transactionData} = props;
    const {t} = useTranslation();
    const [isUTC, setIsUTC] = useState(false);
    const [isValue, setIsValue] = useState(false);
    const [transactionList, setTransactionList] = useState<TransactionListData[]>(
        []
    );
    const [newTransactionList, setNewTransactionList] = useState<
        TransactionListData[]
    >([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState<any>({
        limit: 50,
        page: 1,
    });
    const [totalRecords, setTotalRecords] = useState<any>(0);
    const handle = {
        pagination: (payload: any) => {
            setPagination(payload);
        }
    };
    const _stats = useSelector((e: Object) => (e as any)?.common?.stats);
    const _fiat = useSelector((e: Object) => (e as any)?.common?.fiat);

    const getBlocks = async () => {
        try {
            setLoading(true);
            addressService
                .getAddressTransactions(address, serializeQuery({...pagination}))
                .then((response) => {
                    setTransactionList(response?.items);
                    transactionData({
                        lastTxn: response?.items[0],
                        firstTxn: response?.items[response?.items?.length - 1],
                    });
                    const transformedItems = transformTransactions(response?.items);
                    setNewTransactionList(transformedItems)
                    setTotalRecords(response?.count ?? 0)
                    setLoading(false);
                })
                .catch((e) => setLoading(false));
        } catch (err) {
            console.log(err);
        }
    };
    const transformTransactions = (transactions: TransactionListData[]): TransactionListData[] => {
        return transactions.map((txn) => {
            const nestValue = formatUnit(toXDCNumber(txn?.value, 18, 6))
            const newValue = nestValue.toString();
            const txnFee = formatUnit(toXDCNumber(Number(txn?.gas_price) * Number(txn?.gas_used), 18))
            const newTxnFee = txnFee.toString();
            return {
                ...txn,
                newValue,
                newTxnFee,
            };
        });
    };

    let fileNameAddress = address || "unknown";
    const fileName = `export-address-${fileNameAddress}.csv`;

    const headerList = ['hash', 'method', 'block', 'timestamp', 'from.hash', 'to.hash', 'newValue', 'newTxnFee'];
    const customHeaderList = {
        'hash': 'Hash',
        'method': 'Method',
        'timestamp': 'Timestamp',
        'block': 'Block',
        'from.hash': 'From',
        'to.hash': 'To',
        'newValue': 'Value',
        'newTxnFee': 'TX Fee'
    }
    useEffect(() => {
        getBlocks();
    }, [pagination, address, isUTC , isValue]);
    const handleButtonClick = () => {
        setIsUTC(!isUTC);
    };

       const formatFiat = (
        value: string | number | null | undefined,
        coinPrice: number | string | null | undefined
    ): string => {
        if (!value || (!coinPrice)) return "$0.00";
        const xdcValue = parseFloat(toXDCNumber(value, 18, 4));
        const exchange = parseFloat(String(coinPrice));
        const amount = xdcValue * exchange;
        return getFiat(amount || "0.00");
    };
    const header = useMemo(() => {
        return [
            {
                title: (
                    <BTooltip text={t("See preview of the transaction details.")}>
                        <i className="bi bi-question-circle me-2 ps-2"></i>
                    </BTooltip>
                ),
                param: "header",
                render: (data: any) => (
                    <div>
                        <BTooltip
                            light={true}
                            text={
                                <React.Fragment>
                                    <div className="bs-transaction-additional-info p-2">
                                        <h2 className="bs-card-header-title">
                                            {t("Additional Info")}
                                        </h2>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">{t("Status")}:</p>
                                            <p className="bs-transaction-content">
                                                <TxStatus status={data?.status}/>{" "}
                                                {getPlural("block", data?.confirmations)}{" "}
                                                {t("confirmation")}
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">
                                                {t("Transaction Action")}:
                                            </p>
                                            <p className="bs-transaction-content">
                                                {t("Transfer")}:{" "}
                                                {formatUnit(toXDCNumber(data?.value, 18, 6))}
                                                <br/>
                                                {t("To")}:{" "}
                                                <Link to={`/address/${get0x(data?.to?.hash)}`}>
                                                    <AddressName data={data?.to}/>
                                                </Link>
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">
                                                {t("Transaction Fee")}:
                                            </p>
                                            <p className="bs-transaction-content">
                                                {formatUnit(
                                                    toXDCNumber(data?.gas_price * data?.gas_used, 18)
                                                )}
                                                <BPrice
                                                    value={data?.fee?.value}
                                                    coingeckoID={process.env.REACT_APP_COINGECKO_ID}
                                                    timestamp={getTimeFromNow(data?.timestamp)}
                                                    XDCUSD="XDCUSD"
                                                    decimal={0}
                                                    type=""
                                                />
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">{t("Gas Info")}:</p>
                                            <p className="bs-transaction-content">
                                                {formatNumber(data?.gas_limit || 0)} |{" "}
                                                {formatNumber(data?.gas_used || 0)}
                                                <br/>
                                                {formatUnit(toXDCNumber(data?.gas_price, 18))} (
                                                {toGwei(data?.gas_price)} Gwei)
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">{t("Nonce")}:</p>
                                            <p className="bs-transaction-content">
                                                {data?.nonce} ({t("in the position")} {data?.position})
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <Link to={`/txs/${get0x(data?.hash)}`}>
                                                {t("See more details")} &nbsp;
                                                <i className="bi bi-arrow-up-right"></i>
                                            </Link>
                                        </div>
                                    </div>
                                </React.Fragment>
                            }
                            options={{
                                position: ["right center", "right top", "right bottom"],
                                on: ["click"],
                            }}
                        >
                            <button className="btn btn-sm btn-white fs-70x content-center btn-hov" style={{width: "1.75rem", height: "1.75rem"}}><i className="bi bi-eye"></i>
                            </button>
                        </BTooltip>
                    </div>
                ),
            },
            {
                title: `${t("Tx Hash")}`,
                param: "hash",
                tooltip: (data: any) => (
                    <React.Fragment>
            {data?.status === "ok" ? (
                <Link to={`/tx/${get0x(data?.hash)}`}>{get0x(data?.hash)}</Link>
            ) : null}
                    </React.Fragment>
                ),
                render: (data: any) => (
                    <React.Fragment>
            {data?.status === "error" ? (
                <BTooltip text={t("Error in Main Txn : execution reverted")}>
                    <strong>
                        <i
                            className="bi bi-exclamation-circle text-danger bold"
                            style={{fontWeight: "bold"}}
                        ></i>
                    </strong>
                </BTooltip>
            ) : null}{" "}
                        <Link to={`/tx/${get0x(data?.hash)}`}>
                 {truncate(get0x(data?.hash))}
                            &nbsp;<BCopy value={get0x(data?.hash)} tooltip={`${t("Transaction Hash")}`}/>
            </Link>
                    </React.Fragment>
                ),
            },
            {
                title: `${t("Method")}`,
                param: "method",
                tooltip: true,
                render: (data: any) => (
                    <MethodName data={data}/>
                ),
            },
            {
                title: t("Block"),
                param: "block",
                tooltip: (data: any) => data?.result !== "pending",
                render: (data: any) => (
                    <React.Fragment>
                        <Link to={`/block/${data?.block_number}`}>{data?.block_number}</Link>
                        {(data?.result === "pending" || data?.block_number === null) && (
                            <span className="text-muted text-secondary">(pending)</span>
                        )}
                    </React.Fragment>
                ),
            },
            {
                title: (<a href="javascript:" onClick={handleButtonClick} data-bs-trigger="hover">{isUTC ? t("Date Time (UTC)") : t("Age")}</a>),
                param: "timestamp",
                tooltip: (data: any) => isUTC ? getTimeLang(data?.timestamp, t) : formatTimestamp(data?.timestamp),
                render: (data: any) => isUTC ? formatTimestamp(data?.timestamp) : getTimeLang(data?.timestamp, t),
            },

            {
                title: `${t("From")}`,
                param: "from",
                headerStyle: {minWidth: "130px"},
                copy: (data: any) => getXDC(data?.from?.hash),
                tooltip: (data: any) => {
                    const fromHash = data?.from?.hash?.toLowerCase();
                    const isExcludedAddress = fromHash === address?.toLowerCase();

                    return isExcludedAddress ? (
                        <span>{getXDC(fromHash)}</span>
                    ) : (
                        <Link to={`/address/${get0x(fromHash)}`}>
                            {getXDC(fromHash)}
                        </Link>
                    );
                },
                render: (data: any) => {
                    const fromHash = data?.from?.hash?.toLowerCase();
                    const isExcludedAddress = fromHash === address?.toLowerCase();

                    return isExcludedAddress ? (
                        <AddressName data={data?.from}/>
                    ) : (
                        <Link to={`/address/${get0x(fromHash)}`}>
                            <AddressName data={data?.from}/>
                        </Link>
                    );
                }
            },
            {
                title: "",
                param: "from",
                render: (data: any) => (
                    <React.Fragment>
                        {get0x(data?.from?.hash)?.toLowerCase() === get0x(address)?.toLowerCase() ? <InOut out/> : <InOut/>}
                    </React.Fragment>
                ),
            },
            {
                title: `${t("To")}`,
                param: "to",
                headerStyle: {minWidth: "130px"},
                copy: (data: any) =>
                    !data?.to
                        ? getXDC(data?.created_contract?.hash)
                        : getXDC(data?.to?.hash),
                tooltip: (data: any) => {
                    const toHash = data?.to?.hash?.toLowerCase();
                    const createdContractHash = data?.created_contract?.hash?.toLowerCase();
                    const isExcludedToAddress = toHash === address?.toLowerCase();
                    const isExcludedContractAddress = createdContractHash === address?.toLowerCase();

                    if (!data?.to) {
                        return isExcludedContractAddress ? (
                            <span>{getXDC(createdContractHash)}</span>
                        ) : (
                            <Link to={`/address/${get0x(createdContractHash)}`}>
                                {getXDC(createdContractHash)}
                            </Link>
                        );
                    }

                    return isExcludedToAddress ? (
                        <span>{getXDC(toHash)}</span>
                    ) : (
                        <Link to={`/address/${get0x(toHash)}`}>
                            {getXDC(toHash)}
                        </Link>
                    );
                },
                render: (data: any) => {
                    const toHash = data?.to?.hash?.toLowerCase();
                    const createdContractHash = data?.created_contract?.hash?.toLowerCase();
                    const isExcludedToAddress = toHash === address?.toLowerCase();
                    const isExcludedContractAddress = createdContractHash === address?.toLowerCase();
                
                    if (data?.transaction_types?.includes('contract_creation')) {
                        return isExcludedContractAddress ? (
                            <AddressName data={{transaction_types: ['contract_creation']}}/>
                        ) : (
                            <Link to={`/address/${get0x(createdContractHash)}`}>
                                <AddressName data={{transaction_types: ['contract_creation']}}/>
                            </Link>
                        );
                    }
                
                    if (!data?.to) {
                        return isExcludedContractAddress ? (
                            <AddressName data={data?.created_contract}/>
                        ) : (
                            <Link to={`/address/${get0x(createdContractHash)}`}>
                                <AddressName data={data?.created_contract}/>
                            </Link>
                        );
                    }
                
                    return isExcludedToAddress ? (
                        <AddressName data={data?.to}/>
                    ) : (
                        <Link to={`/address/${get0x(toHash)}`}>
                            <AddressName data={data?.to}/>
                        </Link>
                    );
                }
            }
            ,
            {
                title: (<a href="javascript:" onClick={() => setIsValue(!isValue)}data-bs-trigger="hover"> {isValue ? t("Value (USD)") : t("Amount")}</a>),
                param: "value",
                headerStyle: {minWidth: "130px"},
                tooltip: (data: any) => data?.result !== "pending",
                render: (data: any) => (
                    <React.Fragment>
                {(data?.result === "pending") ? (
                    <span className="text-muted text-secondary" onClick={() => setIsValue(!isValue)}>
                        {isValue  ? getFiat(toXDCNumber(data?.value * Number(_stats?.coin_price)),0,_fiat,true) : formatUnit(toXDCNumber(data?.value, 18, 6))}
                </span>
                ) : (
                    <span onClick={() => setIsValue(!isValue)}>
                        {isValue  ? getFiat(toXDCNumber(data?.value * Number(_stats?.coin_price)),0,_fiat,true) : formatUnit(toXDCNumber(data?.value, 18, 6))}
                </span>  
                )}
                    </React.Fragment>
                )
            },
            {
                title: t("Tx Fee"),
                param: "gas_price",
                tooltip: (data: any) => data?.result !== "pending",
                render: (data: any) => (
                    <React.Fragment>
                    {data?.result === "pending" ? (
                        <span className="text-muted text-secondary">(pending)</span>
                    ) : (
                        formatUnit(toXDCNumber(data?.gas_price * data?.gas_used, 18))
                    )}
                    </React.Fragment>
                ),
            },
        ];
    }, [address, isUTC, isValue]);
    return (
        <BTable2
            title={<React.Fragment>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <p className="mb-0 font-b"><i className="bi bi-sort-down me-1"></i>
                            Latest 25 from a total of <Link to={`/txs?a=${address}`}>
                                {newTransactionList.length}
                            </Link> transactions <br/>
                        </p></div>
                    <div className="d-flex align-items-center gap-2">
                        <CSVConverter data={newTransactionList} fileName={fileName} headers={headerList} customHeaders={customHeaderList}/>
                        <Dropdown className="d-flex bs_token-social" align="end">
                            <Dropdown.Toggle id="dropdown-button-dark-example1" variant="white" size="sm">
                                <i className="bi bi-funnel"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="dark" align="start" className="custom-menu">
                                <Dropdown.Item href={`/txs?a=${address}`} className="bs-small-font">
                                    <i className="bi bi-circle-fill me-1"></i> View Completed Txns
                                </Dropdown.Item>
                                <Dropdown.Item href={`/txsPending?a=${address}&m=hf`} className="bs-small-font">
                                    <i className="bi bi-circle me-1"></i> View Pending Txns
                                </Dropdown.Item>
                                <Dropdown.Item href={`/txs?a=${address}&f=1`} className="bs-small-font">
                                    <i className="bi bi-exclamation-circle me-1"></i> View Failed Txns
                                </Dropdown.Item>
                                <Dropdown.Divider/>
                                <Dropdown.Item href={`/txs?a=${address}&f=2`} className="bs-small-font">
                                    <i className="bi bi-arrow-right me-1"></i> View Outgoing Txns
                                </Dropdown.Item>
                                <Dropdown.Item href={`/txs?a=${address}&f=3`} className="bs-small-font">
                                    <i className="bi bi-arrow-left me-1"></i> View Incoming Txns
                                </Dropdown.Item>
                                <Dropdown.Item href={`/txs?a=${address}&f=5`} className="bs-small-font">
                                    <i className="bi bi-newspaper me-1"></i> View Contract Creation
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
            </React.Fragment>}
            header={header}
            list={transactionList}
            loading={loading}
            pagination={pagination}
            totalRecords={totalRecords}
            onPagination={handle.pagination}
        />
    );
};

export default TransactionAccountList;
